// frontend/src/ui/tp_prod_ord_config.js

/**
 * Tiny template renderer for product order config exports.
 * - Replaces {{tokens}} with values from `data`
 * - Supports nested keys with dot paths (e.g., {{user.name}})
 * - Arrays => joined by ", "
 * - null/undefined => ""
 */

const TOKEN_RE = /{{\s*([\w.]+)\s*}}/g;

function getByPath(obj, path) {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return "";
    cur = cur[p];
  }
  return cur == null ? "" : cur;
}

/**
 * Render a template string with {{tokens}}
 * @param {string} str
 * @param {Record<string, any>} data
 */
export function render(str, data) {
  if (typeof str !== "string") return "";
  return str.replace(TOKEN_RE, (_, key) => {
    const v = getByPath(data, key);
    if (Array.isArray(v)) return v.join(", ");
    if (v == null) return "";
    return String(v);
  });
}

/**
 * Sanitize for filenames (UTF-8 friendly).
 * Keep Unicode; only replace illegal filesystem chars: / \ ? % * : | " < >
 * Collapse spaces; trim; cap length.
 */
export function sanitizeForFilename(s) {
  if (!s) return "";
  return String(s)
    .replace(/[/\\?%*:|"<>]/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

/**
 * Render + sanitize a filename pattern like:
 *   "PRODORDCFG_{{prod_name}}_ops-{{op_label}}_{{ts}}.txt"
 * Trims prod_name to max 20 chars for filenames.
 */
export function renderFilename(pattern, data) {
  const safeData = { ...data };

  if (safeData.prod_name) {
    const name = String(safeData.prod_name).trim();
    safeData.prod_name = name.length > 20 ? `${name.slice(0, 20).trim()}…` : name;
  }

  const raw = render(pattern, safeData);
  const dotIdx = raw.lastIndexOf(".");
  if (dotIdx > 0) {
    const base = raw.slice(0, dotIdx);
    const ext = raw.slice(dotIdx); // includes "."
    return `${sanitizeForFilename(base)}${ext}`;
  }
  return sanitizeForFilename(raw);
}

const tpProdOrdConfig = {
  render,
  renderFilename,
  sanitizeForFilename,
};

export default tpProdOrdConfig;
